import { useState } from "react";
import { ThumbsUp, ThumbsDown, MessageSquare, Loader2, Check } from "lucide-react";
import { submitWidgetFeedback } from "@/lib/api";
import { toast } from "sonner";

/**
 * Sprint S — "Was this useful?" widget.
 *
 * Lightweight inline feedback strip. Shows up + down buttons; on click
 * shows an optional comment field; submits to /api/feedback.
 *
 * @param {"family_story"|"copilot"|"recommendation"|"memory_vault"} surface
 * @param {string=} targetId     stable id for the artifact (story id, rec key, msg id)
 * @param {string=} questionText optional micro-copy override
 * @param {string=} className    optional layout override
 * @param {boolean=} compact     hide title text, only show buttons
 */
export default function FeedbackBar({
  surface,
  targetId,
  questionText = "Was this useful?",
  className = "",
  compact = false,
}) {
  const [vote, setVote] = useState(null); // 'up' | 'down' | null
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);

  const fire = async (v) => {
    setVote(v);
    setSending(true);
    try {
      await submitWidgetFeedback({ surface, vote: v, target_id: targetId });
      setSubmitted(true);
      if (v === "down") {
        setOpen(true);
      } else {
        toast.success("Thanks — got it.");
      }
    } catch {
      toast.error("Could not save your feedback.");
      setVote(null);
    } finally {
      setSending(false);
    }
  };

  const sendComment = async () => {
    if (!comment.trim()) {
      setOpen(false);
      return;
    }
    setSending(true);
    try {
      await submitWidgetFeedback({ surface, vote: vote || "down", target_id: targetId, comment: comment.trim() });
      toast.success("Thanks — we read every comment.");
      setOpen(false);
    } catch {
      toast.error("Could not save your comment.");
    } finally {
      setSending(false);
    }
  };

  if (submitted && !open) {
    return (
      <div
        data-testid={`feedback-thanks-${surface}`}
        className={`inline-flex items-center gap-1.5 text-[11px] text-[var(--fcc-ink-soft)] ${className}`}
      >
        <Check className="w-3 h-3 text-[var(--fcc-green)]" /> Thanks for the feedback.
      </div>
    );
  }

  return (
    <div
      data-testid={`feedback-bar-${surface}`}
      className={`flex flex-wrap items-center gap-2 ${className}`}
    >
      {!compact && (
        <span className="text-[11px] uppercase tracking-widest text-[var(--fcc-muted)]">{questionText}</span>
      )}
      <button
        type="button"
        disabled={sending || vote === "up"}
        onClick={() => fire("up")}
        data-testid={`feedback-up-${surface}`}
        aria-label="Useful"
        className={`p-1.5 rounded-lg border border-[var(--fcc-border)] transition ${
          vote === "up"
            ? "bg-[var(--fcc-green)] text-white border-transparent"
            : "bg-white text-[var(--fcc-ink-soft)] hover:bg-[var(--fcc-cream-2)]"
        }`}
      >
        <ThumbsUp className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        disabled={sending || vote === "down"}
        onClick={() => fire("down")}
        data-testid={`feedback-down-${surface}`}
        aria-label="Not useful"
        className={`p-1.5 rounded-lg border border-[var(--fcc-border)] transition ${
          vote === "down"
            ? "bg-[var(--fcc-ink)] text-white border-transparent"
            : "bg-white text-[var(--fcc-ink-soft)] hover:bg-[var(--fcc-cream-2)]"
        }`}
      >
        <ThumbsDown className="w-3.5 h-3.5" />
      </button>
      {sending && <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--fcc-muted)]" />}
      {open && (
        <div className="flex items-center gap-2 ml-1" data-testid={`feedback-comment-${surface}`}>
          <MessageSquare className="w-3.5 h-3.5 text-[var(--fcc-muted)]" />
          <input
            data-testid={`feedback-comment-input-${surface}`}
            type="text"
            placeholder="Tell us what we got wrong (optional)…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={500}
            className="fcc-input text-xs py-1 px-2 w-56"
            autoFocus
          />
          <button
            type="button"
            onClick={sendComment}
            data-testid={`feedback-comment-submit-${surface}`}
            disabled={sending}
            className="text-[11px] font-semibold px-2 py-1 rounded-lg bg-[var(--fcc-ink)] text-white hover:bg-[var(--fcc-ink-soft)]"
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}
